/**
 * MainHeader Component
 *
 * Description:
 * Main navigation header for the platform. Renders an AppBar with toolbar containing mobile menu, search functionality, and user options. Conditionally displays components based on URL path to provide context-appropriate navigation.
 *
 * Features:
 * - Responsive design with mobile menu integration.
 * - Conditional rendering of search and user options based on current route.
 * - Uses Material-UI AppBar and Toolbar for consistent styling.
 * - URL-based logic to hide/show components in specific sections.
 * - Passes relevant props to custom child components for navigation and actions.
 *
 * Props:
 * (No props required. All logic is based on URL parsing.)
 *
 * Hooks & State:
 * - No hooks or state management. Uses static URL parsing for conditional rendering.
 * - URL parsing: Extracts path segments to determine which components to display.
 *
 * Render Flow:
 * 1. Parses current URL to determine active section.
 * 2. Renders Material-UI AppBar with Toolbar.
 * 3. Always renders MovilMenu component.
 * 4. Conditionally renders Search component (hidden in 'course' and 'lp' sections).
 * 5. Conditionally renders UserOptionsHeader (hidden in 'a', 'course', and 'lp' sections).
 *
 * Good Practices / Warnings:
 * - Component relies on global window object for URL parsing.
 * - Consider using React Router hooks for better integration with routing.
 * - URL-based conditionals may need updates if routing structure changes.
 * - Ensure all child components handle their own props and state management.
 */

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';

import MovilMenu from '../MovilMenu/MovilMenu';
import Search from '../Search/Search';
import UserOptionsHeader from '../UserOptionsHeader/UserOptionsHeader';

import classes from './MainHeader.module.css';

const url = window.location.pathname.split('/');

const Header = () => {
  return (
    <AppBar position="static">
      <Toolbar className={classes.header}>
        <MovilMenu /> {/* Mobile navigation menu component */}
        {url[2] !== 'course' && url[2] !== 'lp' && (
          <Search /> // Search component - hidden in course and landing page sections
        )}
        {url[2] !== 'a' &&
          url[2] !== 'course' &&
          url[2] !== 'lp' && (
            <UserOptionsHeader /> // User options - hidden in academy, course, and landing page sections
          )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
